/**
 * JobPrioritySelect
 *
 * Editable priority control on the job detail page (SC-6369). Jobs arrive from
 * CORE with no priority and fall back to `Unknown`, and until now there was no
 * way to correct that from Works Lite.
 *
 * Options come from the `job_priority` lookup table, so `Unknown` is offered
 * like any other value — it is a real priority meaning "not specified", not an
 * absent one, and a planner may deliberately set it back.
 *
 * Writes via PATCH /jobs/job-detail/:jobId, which updates `priority_id` only
 * and leaves the legacy free-text `job.priority` column untouched.
 */

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Flag } from 'lucide-react';
import { useUpdateJobDetail } from '../hooks/useUpdateJobDetail';
import { useWorkOrderPriorities } from '../hooks/useWorkOrderPriorities';

interface JobPrioritySelectProps {
  jobId: number;
  /** Current `job_priority.id`, or null when the job has no priority row. */
  priorityId: number | null;
  /** Resolved name, used as the placeholder while the lookup list loads. */
  priorityName: string | null;
}

export function JobPrioritySelect({ jobId, priorityId, priorityName }: JobPrioritySelectProps) {
  const { data: priorities, isLoading } = useWorkOrderPriorities();
  const updateJobDetail = useUpdateJobDetail(jobId);

  const handleChange = (value: string) => {
    const nextId = Number(value);
    if (nextId === priorityId) return;
    updateJobDetail.mutate({ priorityId: nextId });
  };

  return (
    <div className="flex items-start gap-2">
      <Flag className="size-4 text-muted-foreground flex-shrink-0" />
      <div className="flex-1">
        <div className="text-xs text-muted-foreground mb-1">Priority</div>
        <Select
          value={priorityId != null ? String(priorityId) : undefined}
          onValueChange={handleChange}
          disabled={isLoading || updateJobDetail.isPending}
        >
          <SelectTrigger className="h-8 text-sm w-full max-w-[220px]" aria-label="Priority">
            <SelectValue placeholder={priorityName ?? 'Not set'} />
          </SelectTrigger>
          <SelectContent>
            {(priorities ?? []).map((priority) => (
              <SelectItem key={priority.id} value={String(priority.id)}>
                {priority.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
